import { utils } from '../../utils';

/**
 * Manages the retrieval of component tracking data from the DOM. Therefore, it iterates all components on a page.
 * For each component, calls registered callbacks, which add attributes to the component tracking object.
 */
class ComponentService {
	/**
	 * @constructor singleton
	 */
	constructor() {
		if (ComponentService._instance) {
			return ComponentService._instance;
		}

		this.registeredCallbacks = [];

		ComponentService._instance = this;
	}

	/**
	 * Adds callback_ to an internal array, that starts with index 1.
	 * If priority_ is true, callback_ is stored at index 0.
	 * So when it comes to merging the data returned by every registered callback, the priority one goes first.
	 * @param {Function} callback_ - adds data to the component tracking by returning them as Object or Array
	 * @param {boolean} priority_ - indicates the default callback
	 * @returns {void}
	 */
	register(callback_, priority_ = false) {
		if (typeof callback_ !== 'function') {
			throw new Error('bad parameter: callback_ is not of type function');
		}

		const index =
			this.registeredCallbacks.length === 0
				? 1
				: this.registeredCallbacks.length;

		if (priority_) {
			this.registeredCallbacks[0] = callback_;
		} else {
			this.registeredCallbacks[index] = callback_;
		}
	}

	/**
	 * The counterpart to register().
	 * @param {Function} callback_ - adds data to the component tracking by returning them as Object or Array
	 * @returns {void}
	 */
	unregister(callback_) {
		let index = this.registeredCallbacks.indexOf(callback_);

		if (index !== -1) {
			this.registeredCallbacks.splice(index, 1);
		}
	}

	/**
	 * Iterates all components on a page. For each component, awaits registered callbacks and adds the data of their
	 * evaluated Promises to the component tracking array.
	 * @returns {Promise<Array>} - Promise that evaluates to component tracking data
	 */
	async getData() {
		const componentElements = document.querySelectorAll('[data-module]');
		const componentElementsLength = componentElements.length;
		let res = [];

		for (
			let componentIndex = 0;
			componentIndex < componentElementsLength;
			componentIndex += 1
		) {
			const componentObjects = [];
			const componentCollections = [];

			for (let getComponentData of this.registeredCallbacks) {
				// note: typeof callback has been checked when registered
				const callbackResult = await getComponentData(
					componentElements[componentIndex],
					componentIndex,
				);

				if (
					callbackResult instanceof Array &&
					callbackResult.length > 0
				) {
					componentCollections.push(callbackResult);
				}
				// note: typeof Array is object as well
				else if (
					typeof callbackResult === 'object' &&
					callbackResult !== []
				) {
					componentObjects.push(callbackResult);
				}
			}

			if (componentCollections.length === 0) {
				res.push(utils.mergeObjects(...componentObjects));
			} else {
				res = res.concat(
					utils.flattenAndMergeObjects(
						componentCollections,
						componentObjects,
					),
				);
			}
		}

		return res;
	}
}

/**
 * Singleton instance
 * @type {ComponentService}
 * @private
 * @static
 */

const componentService = new ComponentService();

export { componentService, ComponentService };
